// src/pages/pengamatan/buah-alami/hooks/useDownloadForm.js
import { useState, useMemo, useEffect } from 'react';
import { ref, onValue } from "firebase/database";
import { database } from "../../../../firebase";

// Hook untuk Download Form - STANDALONE VERSION
export const useDownloadForm = (onStatusUpdate) => {
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedPengamatanData, setSelectedPengamatanData] = useState(null);
  const [pengamatanData, setPengamatanData] = useState([]);
  const [loading, setLoading] = useState(false);

  // AMBIL DATA LANGSUNG DARI FIREBASE
  useEffect(() => {
    if (!downloadDialogOpen) return;

    console.log('🔥 Fetching data from Firebase...');
    setLoading(true);
    
    const pengamatanRef = ref(database, "db_freshpine/db_pengamatan/buah_alami");
    
    const unsubscribe = onValue(pengamatanRef, (snapshot) => {
      try {
        if (snapshot.exists()) {
          const data = snapshot.val();
          console.log('📥 Data received from Firebase');
          
          // Transform data ke array
          const dataArray = Object.entries(data).map(([key, value]) => ({
            pengamatanKey: key,
            id: key,
            ...value
          }));
          
          console.log('📊 Transformed data count:', dataArray.length);
          console.log('📋 Sample data:', dataArray[0]);
          setPengamatanData(dataArray);
        } else {
          console.log('❌ No data in Firebase');
          setPengamatanData([]);
        }
      } catch (error) {
        console.error('❌ Error processing data:', error);
        onStatusUpdate({
          open: true,
          message: "Gagal memproses data dari database",
          severity: "error"
        });
      } finally {
        setLoading(false);
      }
    }, (error) => {
      console.error('❌ Firebase error:', error);
      setLoading(false);
      onStatusUpdate({
        open: true,
        message: "Gagal mengambil data dari database",
        severity: "error"
      });
    });

    return () => unsubscribe();
  }, [downloadDialogOpen, onStatusUpdate]);

  const handleOpenDownloadDialog = () => {
    console.log('🎯 Opening download dialog');
    setDownloadDialogOpen(true);
    setSelectedLocation('');
    setSearchTerm('');
    setSelectedPengamatanData(null);
    setPengamatanData([]);
  };

  const handleCloseDownloadDialog = () => {
    console.log('🚪 Closing download dialog');
    setDownloadDialogOpen(false);
    setSelectedLocation('');
    setSearchTerm('');
    setSelectedPengamatanData(null);
    setPengamatanData([]);
  };

  const handleSearchChange = (event, value) => {
    setSearchTerm(value || '');
  };

  // HANDLE PEMILIHAN LOKASI
  const handleAutocompleteChange = (event, value) => {
    console.log('🔘 Autocomplete selected:', value);
    
    if (value) {
      // Jika value adalah objek (dari dropdown), ambil lokasinya
      const locationValue = typeof value === 'string' ? value : value.lokasi;
      setSelectedLocation(locationValue);
      setSearchTerm('');
      
      // CARI DATA YANG SESUAI DARI DATA FIREBASE
      const foundOrder = pengamatanData.find(order => order.lokasi === locationValue);
      
      console.log('🔍 Search result:', foundOrder ? 'Found' : 'Not found');
      
      if (foundOrder) {
        console.log('✅ Data found:', {
          lokasi: foundOrder.lokasi,
          tanggal: foundOrder.tanggal_pengamatan,
          plotCount: Object.keys(foundOrder.plot || {}).length
        });
        setSelectedPengamatanData(foundOrder);
      } else {
        console.log('❌ Data not found for location:', locationValue);
        setSelectedPengamatanData(null);
      }
    } else {
      setSelectedLocation('');
      setSelectedPengamatanData(null);
    }
  };

  // FILTER LOKASI: Hanya lokasi yang SEMUA plot-nya Processed
  const getProcessedLocations = () => {
    if (!pengamatanData || pengamatanData.length === 0) {
      console.log('📭 No data available');
      return [];
    }

    console.log('🔍 Processing', pengamatanData.length, 'records from Firebase');
    
    const processedLocations = [];
    
    pengamatanData.forEach((order, index) => {
      // Skip jika tidak ada lokasi
      if (!order.lokasi) {
        console.log(`❌ Record ${index}: No location`);
        return;
      }
      
      // Skip jika tidak ada plot data
      if (!order.plot || Object.keys(order.plot).length === 0) {
        console.log(`📭 ${order.lokasi}: No plots data`);
        return;
      }
      
      // Cek apakah SEMUA plot berstatus Processed
      const plotEntries = Object.values(order.plot);
      const allPlotsProcessed = plotEntries.every(
        plot => plot.status_pengamatan_per_plot === 'Processed'
      );
      
      console.log(`📍 ${order.lokasi}: ${plotEntries.length} plots, allProcessed = ${allPlotsProcessed}`);
      
      if (allPlotsProcessed) {
        processedLocations.push({
          lokasi: order.lokasi,
          tanggal_pengamatan: order.tanggal_pengamatan || '-'
        });
        console.log(`✅ ADDED ${order.lokasi} to available locations`);
      }
    });
    
    // Hapus duplikat berdasarkan lokasi dan urutkan
    const uniqueLocations = processedLocations.filter((item, index, self) => 
      index === self.findIndex(t => t.lokasi === item.lokasi)
    ).sort((a, b) => a.lokasi.localeCompare(b.lokasi));
    
    console.log('🎯 Final available locations:', uniqueLocations);
    
    return uniqueLocations;
  };

  const filteredLocations = useMemo(() => {
    const locations = getProcessedLocations();
    
    if (!searchTerm) return locations;
    
    const filtered = locations.filter(location => 
      location.lokasi.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    console.log('🔎 After search filter:', filtered.length, 'locations');
    return filtered;
  }, [searchTerm, pengamatanData]);

  return {
    downloadDialogOpen,
    selectedLocation,
    searchTerm,
    filteredLocations,
    selectedPengamatanData,
    loading,
    handleOpenDownloadDialog,
    handleCloseDownloadDialog,
    handleSearchChange,
    handleAutocompleteChange,
  };
};